import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useEvieChatContext } from '../EvieChatProvider';
import { CHAT_ROLES, getDayLabel, groupMessagesByDay } from '../utils';
import MsgBubble from '../MsgBubble';
import TypingIndicator from '../TypingIndicator';
import NewUserMessage from '../NewUserMessage';
import QuickReplies from '../QuickReplies';

import './messages.scss';

const Messages = ({ messages, userMessage, updateUserMessage }) => {
  const { t, i18n } = useTranslation();
  const groupedMessages = groupMessagesByDay(messages);
  const { quickReplies, isAwaitingResponse, isAwaitingGreeting } = useEvieChatContext();
  const seenIdsRef = useRef(null);
  const [animatingMessageId, setAnimatingMessageId] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (seenIdsRef.current === null) {
      seenIdsRef.current = new Set(messages.map((m) => m.id));
      return;
    }
    const newMsg = messages.find((m) => m.role === CHAT_ROLES.ASSISTANT && !seenIdsRef.current.has(m.id));
    if (newMsg) {
      setAnimatingMessageId(newMsg.id);
      setIsAnimating(true);
    }
    messages.forEach((m) => seenIdsRef.current.add(m.id));
  }, [messages]);

  const onAnimationComplete = useCallback(() => {
    setIsAnimating(false);
    setAnimatingMessageId(null);
  }, []);

  return (
    <ul className="messages">
      {groupedMessages.map(({ dateKey, messages: dayMessages }) => (
        <React.Fragment key={dateKey}>
          <li className="messages__day-separator">
            <span>{getDayLabel(dateKey, t, i18n.language)}</span>
          </li>
          {dayMessages.map((message) => (
            <MsgBubble
              key={message.id}
              message={message}
              isNew={message.id === animatingMessageId}
              onAnimationComplete={onAnimationComplete}
            />
          ))}
        </React.Fragment>
      ))}
      {userMessage && isAwaitingResponse && <NewUserMessage userMessage={userMessage} />}
      {(isAwaitingResponse || isAwaitingGreeting) && <TypingIndicator />}
      {!isAwaitingResponse && !isAnimating && quickReplies.length > 0 && (
        <QuickReplies items={quickReplies} updateUserMessage={updateUserMessage} />
      )}
    </ul>
  );
};

Messages.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      createdAt: PropTypes.string,
      role: PropTypes.oneOf(Object.values(CHAT_ROLES)),
      content: PropTypes.string,
      contentReommendations: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          contentType: PropTypes.oneOf(['exercise', 'audio', 'short_video', 'reading', 'squeeze']),
          title: PropTypes.string,
        }),
      ),
    }),
  ),
  userMessage: PropTypes.string.isRequired,
  updateUserMessage: PropTypes.func.isRequired,
};

Messages.defaultProps = {
  messages: [],
};

export default Messages;
